import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbSEOProps {
  items?: BreadcrumbItem[];
  className?: string;
}

const BreadcrumbSEO = ({ items, className = "" }: BreadcrumbSEOProps) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs if not provided
  const breadcrumbs = items || generateBreadcrumbs(location.pathname);
  
  // Generate structured data for breadcrumbs
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": item.href ? `https://compresorjpeg.com${item.href}` : undefined
    }))
  };

  React.useEffect(() => {
    // Add structured data to head
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    script.id = 'breadcrumb-structured-data';
    
    // Remove existing breadcrumb structured data
    const existing = document.getElementById('breadcrumb-structured-data');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('breadcrumb-structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [breadcrumbs]);

  if (breadcrumbs.length <= 1) {
    return null; // Don't show breadcrumbs for home page
  }

  return (
    <nav 
      className={`flex items-center space-x-2 text-sm text-muted-foreground mb-4 ${className}`}
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center space-x-2">
        {breadcrumbs.map((item, index) => (
          <li key={index} className="flex items-center">
            {index === 0 && <Home className="h-4 w-4 mr-1" />}
            
            {item.href ? (
              <Link 
                to={item.href} 
                className="hover:text-primary transition-colors duration-200"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-foreground font-medium">{item.label}</span>
            )}
            
            {index < breadcrumbs.length - 1 && (
              <ChevronRight className="h-4 w-4 mx-2 text-muted-foreground/60" />
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
  const pathnames = pathname.split('/').filter(x => x);
  
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Inicio', href: '/' }
  ];
  
  const routeNames: Record<string, string> = {
    'faq': 'Preguntas Frecuentes',
    'auth': 'Iniciar Sesión',
    'history': 'Historial',
    'compress': 'Comprimir',
    'about': 'Acerca de',
    'contact': 'Contacto',
    'privacy': 'Privacidad',
    'terms': 'Términos',
    'herramientas': 'Herramientas',
    'blog': 'Blog'
  };
  
  pathnames.forEach((name, index) => {
    const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
    const isLast = index === pathnames.length - 1;
    
    breadcrumbs.push({
      label: routeNames[name] || name.charAt(0).toUpperCase() + name.slice(1),
      href: isLast ? undefined : routeTo
    });
  });
  
  return breadcrumbs;
};

export default BreadcrumbSEO;